import { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Languages, Loader2 } from 'lucide-react';
import { SUPPORTED_LANGUAGES, getLanguageName } from '@/lib/language-config';
import { translatePolicy } from '@/lib/translation-service';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { useNavigate } from 'react-router-dom';
import { ActivityLogger } from '@/lib/activity-logger';

interface TranslationButtonProps {
  policyId: string;
  title: string;
  content: string;
  department: string | null;
  version: number;
  effectiveDate: string | null;
  nextRenewalDate: string | null;
  assignedReviewers: string[] | null;
  assignedApprovers: string[] | null;
  companyId: string;
  userId: string;
}

export function TranslationButton({
  policyId,
  title,
  content,
  department,
  version,
  effectiveDate,
  nextRenewalDate,
  assignedReviewers,
  assignedApprovers,
  companyId,
  userId,
}: TranslationButtonProps) {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState<string | null>(null);
  const [isTranslating, setIsTranslating] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();

  const handleLanguageSelect = (languageCode: string) => {
    setSelectedLanguage(languageCode);
    setIsDialogOpen(true);
  };

  const handleConfirmTranslation = async () => {
    if (!selectedLanguage) return;

    setIsTranslating(true);
    try {
      // Translate the policy
      const { title: translatedTitle, content: translatedContent } = await translatePolicy(
        title,
        content,
        selectedLanguage
      );

      // Create new policy with translated content
      const { data: newPolicy, error: policyError } = await supabase
        .from('policies')
        .insert({
          title: translatedTitle,
          content: translatedContent,
          department: department,
          version: version,
          effective_date: effectiveDate,
          next_renewal_date: nextRenewalDate,
          assigned_reviewers: assignedReviewers,
          assigned_approvers: assignedApprovers,
          company_id: companyId,
          created_by: userId,
          status: 'draft',
          source_policy_id: policyId,
          language: selectedLanguage,
        })
        .select('id')
        .single();

      if (policyError) throw policyError;

      // Log activity
      await ActivityLogger.logPolicyTranslated(
        companyId,
        userId,
        policyId,
        newPolicy.id,
        selectedLanguage,
        title
      );

      toast({
        title: 'Translation Complete',
        description: `Policy translated to ${getLanguageName(selectedLanguage)} successfully.`,
      });

      // Redirect to editor for the translated policy
      navigate(`/policy-creator?edit=${newPolicy.id}`);
    } catch (error: any) {
      console.error('Error translating policy:', error);
      toast({
        title: 'Translation Failed',
        description: error.message || 'Failed to translate policy. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsTranslating(false);
      setIsDialogOpen(false);
      setSelectedLanguage(null);
    }
  };

  const enabledLanguages = SUPPORTED_LANGUAGES.filter(lang => lang.enabled);
  const disabledLanguages = SUPPORTED_LANGUAGES.filter(lang => !lang.enabled);

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" disabled={isTranslating}>
            <Languages className="w-4 h-4 mr-2" />
            Translate into
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {enabledLanguages.map((language) => (
            <DropdownMenuItem
              key={language.code}
              onClick={() => handleLanguageSelect(language.code)}
            >
              {language.name}
            </DropdownMenuItem>
          ))}
          {disabledLanguages.length > 0 && (
            <>
              {disabledLanguages.map((language) => (
                <DropdownMenuItem
                  key={language.code}
                  disabled
                  className="text-muted-foreground opacity-50"
                >
                  {language.name}
                </DropdownMenuItem>
              ))}
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Translate Policy</DialogTitle>
            <DialogDescription>
              This will create a new version of this policy in {selectedLanguage && getLanguageName(selectedLanguage)}. 
              The translated policy will go through the same approval workflow.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setIsDialogOpen(false);
                setSelectedLanguage(null);
              }}
              disabled={isTranslating}
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmTranslation}
              disabled={isTranslating}
            >
              {isTranslating ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Translating...
                </>
              ) : (
                `Translate to ${selectedLanguage && getLanguageName(selectedLanguage)}`
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}












